/**
 * Sequences the reader wrote, encoded by the model and kept on the map (`V137`-`V139`, P29.2).
 *
 * ## What this is, against the two things it is not
 *
 * A point on this map has had exactly two provenances until now. `V14`: a **stored** point is a
 * corpus member at its own position. `V123`: a **generated** point is a sequence the model wrote
 * at a coordinate the reader clicked. This module adds a third and it is neither — **the sequence
 * is the reader's and only the position is the model's**. `panels/detail.ts` gives it its own
 * colour and its own glyph for that reason, and nothing here is ever counted where corpus rows
 * are counted.
 *
 * ## Why anything is stored at all, and why `V123`'s stated reason is not it
 *
 * `V123`'s bar is *persist what cannot be recreated by repeating the gesture*, measured on the
 * fact that `decode.py` seeds nothing so the same click yields a different sequence.
 *
 * **An encoded point does not clear that bar.** `server.py`'s `/encode` is explicit that
 * `SequenceVAE.encode` returns `mu` and never `rsample()`, precisely so that "a user typing the
 * same peptide twice would see it move" cannot happen. Encoding is deterministic; repeating the
 * gesture reproduces the coordinate exactly.
 *
 * A different bar applies, and it is written down here rather than smuggled in under `V123`'s
 * (`V138`):
 *
 * - **What cannot be recreated is the typing, not the coordinate.** A 16-mer of NCAA codes is ~64
 *   characters composed by hand; eighty of them pasted from a spreadsheet is an afternoon. The
 *   unrecoverable artefact is the *request*.
 * - **Repeating the gesture is not free.** A warm `/encode` measured 316.8 ms on
 *   `ancestor_10m_kl003` with a reconstruction and ~48 ms without, so re-deriving 200 points is
 *   ~10 s of round trips on top of a checkpoint load.
 *
 * So: persist, and **the input is the primary field**. Everything else on the record is derivable
 * from `sequence` by one `/encode`, and §"staleness" below is the rule that follows from it.
 *
 * ## Staleness: a rebuild does not delete these (`V139`)
 *
 * `decoded.ts` **deletes** any record whose `builtAt` no longer matches the loaded manifest, and
 * that is right for it: the coordinate *was* the input, a refit makes it name a region of an
 * embedding that no longer exists, and there is nothing underneath worth keeping.
 *
 * Here the input survives the same event, so deleting would lose hand-entered work to an overnight
 * rebuild with no gesture of the reader's involved — which is the loss `V123` exists to prevent,
 * arriving through the mechanism `V123` installed.
 *
 * | | decoded mark | encoded mark |
 * |---|---|---|
 * | the input | a coordinate | **a sequence** |
 * | the output | a sequence | a coordinate |
 * | what a rebuild invalidates | the coordinate, which was the input, so the record | the coordinate, which was the **output**, so the coordinate only |
 * | on a `builtAt` mismatch | **deleted** | **kept, and reported by `staleForView`** |
 *
 * A stale record is **never drawn**: its `xy` belongs to an embedding that is gone, and drawing it
 * is the `V115` failure that put eight landmarks 11,000 px off a 1,200 px canvas. It is listed in
 * the panel with a control that re-encodes it. **Not re-encoded automatically at load**, for the
 * reason `decoded.ts` never replays `POST /decode`: N model round trips nobody asked for.
 *
 * ## The cap refuses; it does not evict (`V139`), and it is 500 rather than 200 (`V142`)
 *
 * `decoded.ts` evicts oldest-first so that a 201st *click* still works. Here the eviction would
 * destroy typing, so `add` returns `null` when the view is full and the caller reports how many
 * slots are free. A bulk paste checks `freeSlots` before issuing a single request, because half an
 * import is a set nobody can tell from a whole one.
 *
 * ## Its own module rather than a widened `decoded.ts`
 *
 * The two rows of the table above are a branch in `forView`, another in the cap policy, and a
 * third in every consumer asking "may I draw this". One key would also mean one payload, so a
 * corrupt write would cost both histories. The storage boilerplate below is duplicated
 * deliberately, and the two modules are written as twins so that a reader of one can read the
 * other.
 *
 * ## Every operation re-reads storage first
 *
 * No in-memory cache, for `decoded.ts`'s reason: two tabs share one store and a cached copy in
 * either would clobber the other's writes.
 */
import type { EncodeResponse } from './api';

/** `v1` in the key, so a future shape change is a new key rather than a parse of a stale record. */
export const STORAGE_KEY = 'peptide-vis.encoded.v1';

/**
 * The most encoded points kept for one `(atlas, projection, build)` (`V142`).
 *
 * **500, where `decoded.ts` caps at 200, and the difference is not an inconsistency.** The two
 * numbers answer different questions because the two kinds of point arrive by different gestures.
 *
 * `decoded.ts`'s 200 is bounded by *legibility*: a decoded point is one click, so 200 of them is a
 * canvas someone filled by clicking around, and past that the map stops being readable. An encoded
 * point arrives from a **list the reader curated** — a screen of peptides from a spreadsheet is one
 * gesture, and a cap that refused the second half of somebody's assay plate would be the tool
 * getting in the way of the thing it exists for.
 *
 * Bounded instead by *storage*, which is where the real ceiling is. Measured: a record without its
 * latent vector is **503 bytes** (`V138`), so
 *
 * | | one view | ten views |
 * |---|---|---|
 * | 200 records | 98 kB | 0.96 MB |
 * | **500 records** | **246 kB** | **2.4 MB** |
 *
 * against a ~5 MB origin quota **shared with `decoded.ts`'s key**. Ten views at the cap is already
 * an unusual amount of work to have done; and if it were ever reached, `commit` degrades to memory
 * rather than losing anything (see `persisting`).
 *
 * **The legibility cost is real and is paid on the map rather than in the store.** 500 glyphs is
 * past what anyone can take in — which is why a bulk import now places its points with their cards
 * **closed** (`openCard` on `add`): 500 triangles is a dense scatter, and 500 open cards would be
 * an unusable page. The composer's single placement still opens its card, because that is the
 * answer to that gesture.
 */
export const CAP_PER_VIEW = 500;

/** What makes two coordinates comparable: one projection, of one build, of one atlas. */
export interface EncodedView {
  atlasId: string;
  projection: string;
  /** `AtlasManifest.built_at`. What makes a coordinate stale — see the module docstring. */
  builtAt: string;
}

export interface EncodedMark extends EncodedView {
  id: string;
  /**
   * Which checkpoint placed it. **Not the invalidator** — `builtAt` is — but the discriminator a
   * stale entry needs to explain itself: if this still matches, the point moved because the
   * projection was refitted; if it does not, the model itself changed, which is a second reason
   * and a different sentence.
   */
  checkpointSha: string;

  /** **The input.** The one field on this record that no request can bring back. */
  sequence: string;
  /** The reader's own name for it — bulk mode's optional second column, `''` from the composer. */
  label: string;

  /** Everything below is re-derivable from `sequence` by one `/encode`. */
  xy: [number, number];
  nearestRow: number;
  nearestDistance: number;
  /**
   * `‖z‖`, computed once here from the response's own vector.
   *
   * **The vector itself is deliberately not stored.** Serialised at `ancestor_10m_kl003`'s
   * `latent_dim = 256` it is ~5.3 kB against a ~500-byte record without it — so at
   * `CAP_PER_VIEW = 500` one full view would be **~2.9 MB with it against ~246 kB without**, which
   * is most of a 5 MB origin quota for a single view of a quota shared with the decoded store.
   * (At the 200 this cap started as it was already ~1.13 MB against ~98 kB; raising the cap did
   * not create the problem, it widened it.) What keeping `z` would buy is re-decoding an encoded
   * point under another sampler, and that costs one `/encode` to recover because the sequence is
   * right here.
   */
  zNorm: number;

  /** ISO instant of the encode. */
  at: string;
  /** Whether the on-map card is open beside the glyph. The glyph itself is always drawn. */
  card: boolean;
}

export interface EncodedStore {
  /**
   * Every mark for one view, oldest first — **the ones that are safe to draw**.
   *
   * Unlike `decoded.ts`'s, this prunes nothing: a record of another build is stale, not garbage,
   * and `staleForView` is where it surfaces.
   */
  forView(view: EncodedView): EncodedMark[];
  /**
   * The records for this atlas and projection whose **build no longer matches** — kept, not drawn,
   * and re-placeable by one `/encode` each.
   */
  staleForView(view: EncodedView): EncodedMark[];
  /** How many more may be added to this view before `add` starts refusing. */
  freeSlots(view: EncodedView): number;
  /**
   * Record one encode, or **`null` when this view is full** (the module docstring's cap rule).
   *
   * Re-encoding a sequence already placed on this view **replaces** its record and keeps its id,
   * so the walk entry and the card that name it survive: encoding is deterministic, so a second
   * record would be a second glyph at the same coordinate with one of them unreachable.
   */
  add(
    view: EncodedView,
    checkpointSha: string,
    sequence: string,
    label: string,
    result: EncodeResponse,
    /**
     * Whether the point's card opens on the map.
     *
     * **`true` for one placement and `false` for a bulk import** (`V142`), which is the whole
     * reason this is a parameter rather than a constant. Opening one card answers the gesture that placed it;
     * opening five hundred blankets the map with them. A re-place keeps whatever the reader had.
     */
    openCard: boolean,
  ): EncodedMark | null;
  remove(id: string): void;
  /**
   * Forget several in one write. **Ids rather than a wipe**, for `decoded.ts`'s reason: the
   * control is labelled with what is on screen, and emptying the key would silently drop the
   * points of every atlas the reader is not currently looking at.
   */
  removeMany(ids: readonly string[]): void;
  /** Open or close one mark's on-map card, so a reload restores what the reader left. */
  setCard(id: string, open: boolean): void;
}

/**
 * `localStorage`, or `null` when it cannot be reached.
 *
 * **The property access itself throws** under some privacy settings, which is why this is a `try`
 * rather than a null check.
 */
function defaultStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const isXY = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === 'number' &&
  typeof value[1] === 'number' &&
  Number.isFinite(value[0]) &&
  Number.isFinite(value[1]);

/**
 * Whether one parsed entry is usable.
 *
 * **Per record, not per payload**, as `decoded.ts` has it: one entry written by an older build
 * must cost that entry and not the reader's whole history. Only a payload that is not an array at
 * all is discarded wholesale.
 *
 * **`sequence` is checked for non-emptiness and the derived fields are not checked for
 * plausibility.** The asymmetry is the module's thesis: a record whose input survived is
 * repairable by one request, and a record without one is not a record.
 */
function isMark(value: unknown): value is EncodedMark {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m['id'] === 'string' &&
    typeof m['atlasId'] === 'string' &&
    typeof m['projection'] === 'string' &&
    typeof m['builtAt'] === 'string' &&
    typeof m['checkpointSha'] === 'string' &&
    typeof m['sequence'] === 'string' &&
    m['sequence'] !== '' &&
    typeof m['label'] === 'string' &&
    typeof m['at'] === 'string' &&
    typeof m['card'] === 'boolean' &&
    typeof m['nearestRow'] === 'number' &&
    typeof m['nearestDistance'] === 'number' &&
    typeof m['zNorm'] === 'number' &&
    isXY(m['xy'])
  );
}

const sameView = (a: EncodedView, b: EncodedView): boolean =>
  a.atlasId === b.atlasId && a.projection === b.projection && a.builtAt === b.builtAt;

/**
 * Same atlas and projection, so the same *frame* — whether or not the build still matches.
 *
 * **The frame, not the view, is what a sequence is unique within**, and the distinction is what
 * makes re-placing a stale record work. A record answers "where does this peptide sit on this
 * projection of this atlas"; `builtAt` only says which fit computed the answer. So two records for
 * one sequence in one frame are always a duplicate — after a re-place the older one is strictly
 * superseded, and leaving it would show the composer a stale entry it had just fixed.
 */
const sameFrame = (mark: EncodedMark, view: EncodedView): boolean =>
  mark.atlasId === view.atlasId && mark.projection === view.projection;

/** `‖z‖` from the response's own vector, so the card can print it without storing 256 floats. */
export const normOf = (z: readonly number[]): number =>
  Math.sqrt(z.reduce((total, v) => total + v * v, 0));

/**
 * A fresh record id.
 *
 * **The random component is not decoration.** `decoded.ts` mints ids from `Date.now()` and a
 * per-store counter, which is unique within one store and *not* across two: a store constructed
 * after a reload restarts its counter at zero, so two stores over the same storage in the same
 * millisecond mint the same string. Records are keyed by id and a collision silently overwrites
 * one with the other. Six random characters is ~2 billion values per millisecond, which closes it.
 *
 * The timestamp is kept in front because it makes the store readable when it is dumped, and
 * because the id sorts roughly by age.
 */
let idCounter = 0;
function newId(): string {
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * The store, over an injectable `Storage`.
 *
 * The parameter is what makes this unit-testable: the tests pass a stub that can be made to throw
 * on `setItem`, which is the failure that matters and the one a real browser will not produce on
 * demand. `null` gives a store that keeps nothing for the life of the page — the honest
 * degradation when the browser will not let us write.
 */
export function createEncodedStore(storage: Storage | null = defaultStorage()): EncodedStore {
  /**
   * Whether writes are still landing.
   *
   * **Latching, and it has to be**, for `decoded.ts`'s reason: a store that only checked
   * `storage === null` reads back the *old* contents after a refused write, so every add would
   * erase the one before it.
   */
  let persisting = storage !== null;

  /** The full list, kept only once `persisting` has gone false. Never a cache while it is true. */
  const volatileMarks: EncodedMark[] = [];

  function read(): EncodedMark[] {
    if (!persisting || storage === null) return [...volatileMarks];
    let raw: string | null = null;
    try {
      raw = storage.getItem(STORAGE_KEY);
    } catch {
      persisting = false;
      return [...volatileMarks];
    }
    if (raw === null) return [];
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(isMark) : [];
    } catch {
      return [];
    }
  }

  /**
   * Make `next` the whole of the store, in memory if it cannot be in storage.
   *
   * One place decides what happens when the browser refuses, and the answer is never "throw": the
   * caller is holding a sequence the reader typed.
   */
  function commit(next: readonly EncodedMark[]): void {
    if (persisting && storage !== null) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(next));
        return;
      } catch {
        persisting = false;
      }
    }
    volatileMarks.length = 0;
    volatileMarks.push(...next);
  }

  return {
    forView(view) {
      return read().filter((m) => sameView(m, view));
    },

    staleForView(view) {
      return read().filter((m) => sameFrame(m, view) && m.builtAt !== view.builtAt);
    },

    freeSlots(view) {
      return Math.max(0, CAP_PER_VIEW - read().filter((m) => sameView(m, view)).length);
    },

    add(view, checkpointSha, sequence, label, result, openCard) {
      const marks = read();
      // Deterministic encoding means one sequence is one point in one frame (`V137`). **Matched on
      // the frame rather than the view**, so that re-encoding a stale record replaces it instead
      // of leaving the superseded one beside its replacement — see `sameFrame`.
      //
      // The existing record's id is kept, so a walk entry or an open card naming it is not
      // orphaned by a re-place.
      const existing = marks.find((m) => sameFrame(m, view) && m.sequence === sequence);
      if (existing === undefined && marks.filter((m) => sameView(m, view)).length >= CAP_PER_VIEW) {
        return null;
      }
      const mark: EncodedMark = {
        ...view,
        id: existing?.id ?? newId(),
        checkpointSha,
        sequence,
        label,
        xy: result.xy,
        nearestRow: result.nearest_row,
        nearestDistance: result.nearest_distance,
        zNorm: normOf(result.z),
        at: new Date().toISOString(),
        // A re-place keeps the card the reader had open or closed; a new point opens its card
        // only if the caller asked, which the bulk importer does not.
        card: existing?.card ?? openCard,
      };
      commit([...marks.filter((m) => m.id !== mark.id), mark]);
      return mark;
    },

    remove(id) {
      commit(read().filter((m) => m.id !== id));
    },

    removeMany(ids) {
      const dropped = new Set(ids);
      if (dropped.size === 0) return;
      commit(read().filter((m) => !dropped.has(m.id)));
    },

    setCard(id, open) {
      commit(read().map((m) => (m.id === id ? { ...m, card: open } : m)));
    },
  };
}
